import { ResourceStock, ResourceType } from '../systems/ResourceManager';

// === DÉFINITIONS DES EXPÉDITIONS IDLE ===
// Les survivants partent en expédition pendant que le joueur fait un run.
// Résultat calculé au retour du run. Risque de mort (permadeath).

export type ExpeditionId = 'coastal' | 'mine' | 'wreck' | 'abyss';

/** Fourchette min-max */
export interface Range {
  min: number;
  max: number;
}

/** Récompense en ressources (type + quantité aléatoire) */
export interface ResourceReward {
  type: ResourceType;
  amount: Range;
  /** Chance d'obtenir cette ressource (0-1, défaut 1) */
  chance?: number;
}

/** Définition d'une expédition */
export interface ExpeditionDefinition {
  id: ExpeditionId;
  name: string;
  emoji: string;
  description: string;
  /** Niveau de Vigie requis */
  vigieLevel: number;
  /** Nombre min/max de survivants à envoyer */
  minSurvivors: number;
  maxSurvivors: number;
  /** Récompenses en ressources */
  resources: ResourceReward[];
  /** Récompense en or */
  gold: Range;
  /** Chance de loot item rare (0-1) */
  itemChance: number;
  /** Risque de mort par survivant (min-max %, sera réduit par le score) */
  deathRisk: Range;
  /** Seuil de score pour réduction maximale du risque */
  scoreThreshold: number;
}

export const EXPEDITION_DEFINITIONS: Record<ExpeditionId, ExpeditionDefinition> = {

  coastal: {
    id: 'coastal',
    name: 'Fouille côtière',
    emoji: '🏖️',
    description: 'Fouiller les plages pour des matériaux. Sans danger.',
    vigieLevel: 1,
    minSurvivors: 1,
    maxSurvivors: 2,
    resources: [
      { type: 'wood', amount: { min: 2, max: 4 } },
      { type: 'cloth', amount: { min: 1, max: 2 } },
    ],
    gold: { min: 5, max: 15 },
    itemChance: 0,
    deathRisk: { min: 0, max: 0 },
    scoreThreshold: 1,
  },

  mine: {
    id: 'mine',
    name: 'Mine abandonnée',
    emoji: '⛏️',
    description: 'Extraire du fer dans les galeries effondrées.',
    vigieLevel: 1,
    minSurvivors: 1,
    maxSurvivors: 2,
    resources: [
      { type: 'iron', amount: { min: 3, max: 6 } },
      { type: 'crystal', amount: { min: 1, max: 1 }, chance: 0.2 },
    ],
    gold: { min: 10, max: 20 },
    itemChance: 0,
    deathRisk: { min: 5, max: 15 },
    scoreThreshold: 30,
  },

  wreck: {
    id: 'wreck',
    name: 'Épave maudite',
    emoji: '🏴‍☠️',
    description: 'Explorer une épave hantée. Dangereux mais profitable.',
    vigieLevel: 2,
    minSurvivors: 1,
    maxSurvivors: 3,
    resources: [
      { type: 'crystal', amount: { min: 1, max: 2 } },
      { type: 'cursed_bone', amount: { min: 1, max: 1 }, chance: 0.5 },
    ],
    gold: { min: 15, max: 30 },
    itemChance: 0.4,
    deathRisk: { min: 15, max: 30 },
    scoreThreshold: 50,
  },

  abyss: {
    id: 'abyss',
    name: 'Abîme',
    emoji: '🌊',
    description: 'Plonger dans les profondeurs. Richesses ou mort.',
    vigieLevel: 2,
    minSurvivors: 2,
    maxSurvivors: 3,
    resources: [
      { type: 'cursed_bone', amount: { min: 2, max: 3 } },
      { type: 'crystal', amount: { min: 1, max: 2 } },
    ],
    gold: { min: 20, max: 40 },
    itemChance: 0.3,
    deathRisk: { min: 25, max: 50 },
    scoreThreshold: 70,
  },
};

/** Ordre d'affichage */
export const EXPEDITION_ORDER: ExpeditionId[] = ['coastal', 'mine', 'wreck', 'abyss'];

/** Retourne les expéditions disponibles selon le niveau de vigie */
export function getAvailableExpeditions(vigieLevel: number): ExpeditionDefinition[] {
  return EXPEDITION_ORDER
    .map(id => EXPEDITION_DEFINITIONS[id])
    .filter(e => e.vigieLevel <= vigieLevel);
}
